import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import { YOUTUBE_MADE_FOR_KIDS_URL } from 'fym-shared/src/music24/youtubePostSettings';
import { TypedMemo } from '../../../common/types';
import { Panel } from '../../components/primitives/Panel';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { usePromoteChannels } from '../hooks/usePromoteChannels';
import { youtubeMessages } from '../composer/youtube/youtubeMessages';
import { publishMessages } from './publishMessages';
import { PublishTimingField } from './PublishTimingField';
import type { StudioPublishChoice } from './studioPublishChoice';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- the switch uses its own slice */
import styles from './Publish.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StudioPublishSwitchProps {
  accountId: number;
  releaseDate: string | null;
  value: StudioPublishChoice;
  onChange: (value: StudioPublishChoice) => void;
}

/**
 * "Publish to YouTube when ready" on the details of a full-song video (owner decision
 * 2026-10-09). Only with a connected YouTube channel; else a "Connect YouTube" link. When it is on,
 * the user picks the time and the "made for kids" answer, and "Make it" leaves a waiting record:
 * the server uploads the video when the render ends.
 */
const StudioPublishSwitchComponent: React.FC<StudioPublishSwitchProps> = ({
  accountId,
  releaseDate,
  value,
  onChange,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { channels } = usePromoteChannels({ accountId });
  const youtube = useMemo(() => channels.find((channel) => channel.platform === 'youtube'), [channels]);
  const isConnected = youtube?.status === 'connected' && youtube.connection !== null;
  const toggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, enabled: event.target.checked }),
    [onChange, value]
  );

  if (!isConnected) {
    return (
      <Panel>
        <button type="button" className="btn btn-link p-0" onClick={() => router.push(SMLRoutes.promoteChannels)}>
          {intl.formatMessage(publishMessages.studioConnect)}
        </button>
      </Panel>
    );
  }

  return (
    <Panel>
      <div className={styles.switchRow} data-testid="studio-publish-switch">
        <div className="form-check form-switch mb-0">
          <Input
            id="studio-publish-youtube"
            type="switch"
            className="form-check-input"
            checked={value.enabled}
            onChange={toggle}
          />
          <label className="form-check-label fw-semibold" htmlFor="studio-publish-youtube">
            {intl.formatMessage(publishMessages.studioSwitch)}
          </label>
          <small className="d-block text-muted">
            {intl.formatMessage(publishMessages.studioSwitchHint, {
              name: youtube.connection?.displayName ?? youtube.handle ?? 'YouTube',
            })}
          </small>
        </div>
        {value.enabled && (
          <>
            <PublishTimingField
              channel="youtube"
              releaseDate={releaseDate}
              value={value.timing}
              onChange={(timing) => onChange({ ...value, timing })}
              name="studio-publish-when"
            />
            <fieldset className="d-flex flex-column gap-2">
              <legend className="fs-6 fw-semibold mb-1">{intl.formatMessage(youtubeMessages.kidsLabel)}</legend>
              {[true, false].map((answer) => (
                <div className="form-check" key={String(answer)}>
                  <Input
                    id={`studio-kids-${String(answer)}`}
                    type="radio"
                    name="studio-kids"
                    className="form-check-input"
                    checked={value.madeForKids === answer}
                    onChange={() => onChange({ ...value, madeForKids: answer })}
                  />
                  <label className="form-check-label" htmlFor={`studio-kids-${String(answer)}`}>
                    {intl.formatMessage(answer ? youtubeMessages.kidsYes : youtubeMessages.kidsNo)}
                  </label>
                </div>
              ))}
              <small className="text-muted">
                {withBrandWord(intl.formatMessage(youtubeMessages.kidsNote))}{' '}
                <a href={YOUTUBE_MADE_FOR_KIDS_URL} target="_blank" rel="noreferrer">
                  {intl.formatMessage(youtubeMessages.kidsLearnMore)}
                </a>
              </small>
            </fieldset>
          </>
        )}
      </div>
    </Panel>
  );
};

export const StudioPublishSwitch = TypedMemo(StudioPublishSwitchComponent);
